import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const InteractiveHeatmap = ({ imageSrc, regions, infoPoint, altText }) => {
    const { t } = useTranslation();
    const [activeTooltip, setActiveTooltip] = useState(null);

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                
                <img 
                    src={imageSrc} 
                    alt={altText || t('industryApplicationsAmazon.interactive.heatmap.altText')} 
                    style={{ maxWidth: '100%', maxHeight: '350px', width: 'auto', height: 'auto', display: 'block' }}
                />

                <div 
                    style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
                        backgroundColor: activeTooltip && activeTooltip.id !== 'info' ? 'rgba(24, 31, 41, 0.4)' : 'transparent',
                        backdropFilter: activeTooltip && activeTooltip.id !== 'info' ? 'blur(3px)' : 'none',
                        WebkitBackdropFilter: activeTooltip && activeTooltip.id !== 'info' ? 'blur(3px)' : 'none',
                        transition: 'background-color 0.3s, backdrop-filter 0.3s',
                        pointerEvents: 'none', zIndex: 1
                    }} 
                />

                <img 
                    src={imageSrc} 
                    alt="" 
                    style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
                        opacity: activeTooltip && activeTooltip.id !== 'info' ? 1 : 0,
                        transition: 'opacity 0.2s, clip-path 0.2s',
                        pointerEvents: 'none', zIndex: 2,
                        clipPath: activeTooltip && activeTooltip.id !== 'info' && activeTooltip.box 
                            ? `inset(${activeTooltip.box[1] * 100}% ${100 - (activeTooltip.box[2] * 100)}% ${100 - (activeTooltip.box[3] * 100)}% ${activeTooltip.box[0] * 100}%)` 
                            : 'none'
                    }} 
                />

                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 3 }}>
                    {regions && regions.map((region) => {
                        const isHoveringRegion = activeTooltip && activeTooltip.id !== 'info';
                        const isDimmed = isHoveringRegion && activeTooltip.id !== region.id;
                        
                        return (
                            <Tooltip
                                key={region.id}
                                placement={window.innerWidth < 900 ? 'top' : region.position}
                                arrow
                                disableFocusListener
                                disableTouchListener={false}
                                enterTouchDelay={0}
                                leaveTouchDelay={3000}
                                componentsProps={{
                                    tooltip: { sx: { bgcolor: '#171F29', border: '1px solid #F44336', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', p: 1.5, maxWidth: 250 } },
                                    arrow: { sx: { color: '#171F29' } }
                                }}
                                title={
                                    <div style={{ textAlign: 'left', fontFamily: 'Roboto, sans-serif' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '6px', fontSize: '0.75rem', color: '#F44336', letterSpacing: '0.5px' }}>
                                            {t('industryApplicationsAmazon.interactive.heatmap.severityLabel', { severity: region.severity })}
                                        </div>
                                        <div style={{ color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                            {region.reason}
                                        </div>
                                    </div>
                                }
                            >
                                <div 
                                    style={{ 
                                        position: 'absolute', cursor: 'help',
                                        left: `${region.box[0] * 100}%`, top: `${region.box[1] * 100}%`,
                                        width: `${(region.box[2] - region.box[0]) * 100}%`, height: `${(region.box[3] - region.box[1]) * 100}%`,
                                        boxShadow: 'inset 0 0 0 2px rgba(255, 255, 255, 0.5)',
                                        backgroundColor: 'rgba(255, 80, 80, 0.1)',
                                        borderRadius: '6px',
                                        opacity: isDimmed ? 0.2 : 1,
                                        transition: 'all 0.2s ease-out'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 80, 80, 0.2)';
                                        e.currentTarget.style.boxShadow = '0 0 20px 5px rgba(255, 100, 100, 0.85), inset 0 0 0 2px rgba(255, 255, 255, 0.7)';
                                        e.currentTarget.style.transform = 'scale(1.03)';
                                        setActiveTooltip(region);
                                    }} 
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 80, 80, 0.1)';
                                        e.currentTarget.style.boxShadow = 'inset 0 0 0 2px rgba(255, 255, 255, 0.5)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                        setActiveTooltip(null);
                                    }}
                                />
                            </Tooltip>
                        );
                    })}

                    {infoPoint && (
                        <Tooltip
                            placement={window.innerWidth < 900 ? 'top' : infoPoint.position}
                            arrow
                            disableFocusListener
                            disableTouchListener={false}
                            enterTouchDelay={0}
                            leaveTouchDelay={3000}
                            componentsProps={{
                                tooltip: { sx: { bgcolor: '#171F29', border: '1px solid var(--secondary-color)', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', p: 1.5, maxWidth: 320 } },
                                arrow: { sx: { color: '#171F29' } }
                            }}
                            title={
                                <div style={{ textAlign: 'left', fontFamily: 'Roboto, sans-serif' }}>
                                    <div style={{ fontWeight: 700, marginBottom: '6px', fontSize: '0.75rem', color: 'var(--secondary-color)', letterSpacing: '0.5px' }}>
                                        {infoPoint.title}
                                    </div>
                                    <div style={{ color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                        {infoPoint.reason}
                                    </div>
                                </div>
                            }
                        >
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: (activeTooltip && activeTooltip.id !== 'info') ? 0.2 : 1 }}
                                transition={{ delay: 0.8, type: 'spring' }}
                                style={{ 
                                    position: 'absolute', transform: 'translate(-50%, -50%)',
                                    left: `${infoPoint.box[0] * 100}%`, top: `${infoPoint.box[1] * 100}%`,
                                    width: '24px', height: '24px', backgroundColor: 'var(--secondary-color)',
                                    border: '2px solid white', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'help', pointerEvents: 'all', zIndex: 4,
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.5)', boxSizing: 'border-box'
                                }}
                                onMouseEnter={() => setActiveTooltip(infoPoint)}
                                onMouseLeave={() => setActiveTooltip(null)}
                            >
                                <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', fontFamily: 'serif', marginTop: '-1px' }}>i</span>
                            </motion.div>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InteractiveHeatmap;